import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Edit,
  Save,
  X,
  Settings,
  ChevronDown,
  ChevronRight,
  Palette,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import Sidebar from "@/components/Sidebar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Predefined color options for dropdown values
const COLOR_OPTIONS = [
  { value: "bg-gray-100 text-gray-700", label: "Gray", preview: "bg-gray-100" },
  { value: "bg-blue-100 text-blue-700", label: "Blue", preview: "bg-blue-100" },
  { value: "bg-green-100 text-green-700", label: "Green", preview: "bg-green-100" },
  { value: "bg-emerald-100 text-emerald-700", label: "Emerald", preview: "bg-emerald-100" },
  { value: "bg-amber-100 text-amber-700", label: "Amber", preview: "bg-amber-100" },
  { value: "bg-yellow-100 text-yellow-700", label: "Yellow", preview: "bg-yellow-100" },
  { value: "bg-orange-100 text-orange-700", label: "Orange", preview: "bg-orange-100" },
  { value: "bg-red-100 text-red-700", label: "Red", preview: "bg-red-100" },
  { value: "bg-purple-100 text-purple-700", label: "Purple", preview: "bg-purple-100" },
  { value: "bg-pink-100 text-pink-700", label: "Pink", preview: "bg-pink-100" },
  { value: "bg-cyan-100 text-cyan-700", label: "Cyan", preview: "bg-cyan-100" },
  { value: "bg-teal-100 text-teal-700", label: "Teal", preview: "bg-teal-100" },
];

function FieldSection({ config, onAddOption, onUpdateOption, onDeleteOption }) {
  const [isOpen, setIsOpen] = useState(true);
  const [editingOption, setEditingOption] = useState(null);
  const [editForm, setEditForm] = useState({ label: "", color: "" });
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newOption, setNewOption] = useState({
    value: "",
    label: "",
    color: COLOR_OPTIONS[0].value,
  });

  const handleStartEdit = (option) => {
    setEditingOption(option.value);
    setEditForm({ label: option.label, color: option.color });
  };

  const handleSaveEdit = async (optionValue) => {
    await onUpdateOption(config.field_name, optionValue, editForm);
    setEditingOption(null);
  };

  const handleAddOption = async () => {
    if (!newOption.value || !newOption.label) {
      toast.error("Value and Label are required");
      return;
    }
    await onAddOption(config.field_name, newOption);
    setNewOption({ value: "", label: "", color: COLOR_OPTIONS[0].value });
    setAddDialogOpen(false);
  };

  return (
    <Card className="mb-4">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isOpen ? (
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                )}
                <div>
                  <CardTitle className="text-lg">{config.display_name}</CardTitle>
                  <CardDescription className="text-xs font-mono">
                    {config.field_name}
                  </CardDescription>
                </div>
              </div>
              <Badge variant="outline">{config.options?.length || 0} options</Badge>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0">
            <div className="space-y-2">
              {(config.options || []).map((option) => (
                <div
                  key={option.value}
                  className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                >
                  {editingOption === option.value ? (
                    <div className="flex items-center gap-3 flex-1">
                      <Input
                        value={editForm.label}
                        onChange={(e) =>
                          setEditForm({ ...editForm, label: e.target.value })
                        }
                        className="h-8 w-32"
                        placeholder="Label"
                      />
                      <Select
                        value={editForm.color}
                        onValueChange={(value) =>
                          setEditForm({ ...editForm, color: value })
                        }
                      >
                        <SelectTrigger className="h-8 w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {COLOR_OPTIONS.map((color) => (
                            <SelectItem key={color.value} value={color.value}>
                              <div className="flex items-center gap-2">
                                <span
                                  className={`w-3 h-3 rounded ${color.preview}`}
                                />
                                {color.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleSaveEdit(option.value)}
                      >
                        <Save className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingOption(null)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3">
                        <Badge className={option.color}>{option.label}</Badge>
                        <span className="text-xs font-mono text-muted-foreground">
                          {option.value}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => handleStartEdit(option)}
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Option</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete &quot;{option.label}&quot;? This
                                won&apos;t affect existing records with this value.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() =>
                                  onDeleteOption(config.field_name, option.value)
                                }
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Add Option Button */}
            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
              <Button
                variant="outline"
                size="sm"
                className="mt-4 w-full"
                onClick={() => setAddDialogOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Option
              </Button>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Option to {config.display_name}</DialogTitle>
                  <DialogDescription>
                    Create a new dropdown option for {config.field_name}
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="space-y-2">
                    <Label>Value (internal, no spaces)</Label>
                    <Input
                      value={newOption.value}
                      onChange={(e) =>
                        setNewOption({
                          ...newOption,
                          value: e.target.value.toUpperCase().replace(/\s/g, "_"),
                        })
                      }
                      placeholder="e.g., NEW_STATUS"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Label (display name)</Label>
                    <Input
                      value={newOption.label}
                      onChange={(e) =>
                        setNewOption({ ...newOption, label: e.target.value })
                      }
                      placeholder="e.g., New Status"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Color</Label>
                    <Select
                      value={newOption.color}
                      onValueChange={(value) =>
                        setNewOption({ ...newOption, color: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COLOR_OPTIONS.map((color) => (
                          <SelectItem key={color.value} value={color.value}>
                            <div className="flex items-center gap-2">
                              <span className={`w-4 h-4 rounded ${color.preview}`} />
                              {color.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {newOption.value && newOption.label && (
                    <div className="p-3 bg-muted rounded-lg">
                      <Label className="text-xs text-muted-foreground">Preview</Label>
                      <div className="mt-2">
                        <Badge className={newOption.color}>{newOption.label}</Badge>
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleAddOption}>Add Option</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

export default function DropdownManagerPage() {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/dropdown-options`);
      setConfigs(response.data);
    } catch (error) {
      console.error("Error fetching dropdown configs:", error);
      toast.error("Failed to load dropdown configurations");
    } finally {
      setLoading(false);
    }
  };

  const handleAddOption = async (fieldName, option) => {
    try {
      await axios.post(`${API}/dropdown-options/${fieldName}/add`, option);
      toast.success("Option added successfully");
      fetchConfigs();
    } catch (error) {
      console.error("Error adding option:", error);
      toast.error(error.response?.data?.detail || "Failed to add option");
    }
  };

  const handleUpdateOption = async (fieldName, optionValue, updates) => {
    try {
      await axios.put(`${API}/dropdown-options/${fieldName}/${optionValue}`, updates);
      toast.success("Option updated successfully");
      fetchConfigs();
    } catch (error) {
      console.error("Error updating option:", error);
      toast.error("Failed to update option");
    }
  };

  const handleDeleteOption = async (fieldName, optionValue) => {
    try {
      await axios.delete(`${API}/dropdown-options/${fieldName}/${optionValue}`);
      toast.success("Option deleted successfully");
      fetchConfigs();
    } catch (error) {
      console.error("Error deleting option:", error);
      toast.error("Failed to delete option");
    }
  };

  return (
    <div className="flex h-screen overflow-hidden" data-testid="dropdown-manager-page">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="glass-header px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <Settings className="h-6 w-6 text-primary" />
                Dropdown Manager
              </h1>
              <p className="text-sm text-muted-foreground">
                Customize dropdown options for Order Status, Account, Design Status, and more
              </p>
            </div>
          </div>
        </header>

        {/* Content */}
        <ScrollArea className="flex-1 p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : configs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Settings className="h-16 w-16 text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground">No dropdown configurations found</p>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto">
              {/* Info Banner */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-medium text-blue-900 mb-1">How it works</h3>
                <p className="text-sm text-blue-700">
                  Add, edit, or remove options from dropdown fields used throughout the app.
                  Changes are reflected immediately in the Orders page, Order Sidebar, and
                  filter dropdowns. Deleting an option won&apos;t affect existing records.
                </p>
              </div>

              {/* Field Sections */}
              {configs.map((config) => (
                <FieldSection
                  key={config.field_name}
                  config={config}
                  onAddOption={handleAddOption}
                  onUpdateOption={handleUpdateOption}
                  onDeleteOption={handleDeleteOption}
                />
              ))}
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
}
